import { Box, Chip, Typography, Button } from "@mui/joy";
import { CheckCircle2, Clock, PackageCheck, Mail, RotateCw, Download } from "lucide-react";
import { useEffect } from "react";
import moment from "moment";

import { DocumentPreviewWidget, type IMenuAction } from "../../";
import TaskActionComponent from "../../../components/Tasks/components/TaskAction";
import { useDocument, useReport } from "../../../hooks/query";
import { useProject } from "../../../hooks";
import { useNotification } from "../../../hooks/useNotification";

import { useContractSigning } from "./hooks";

interface ITaskComponent {
    activeTask: any;
    measurements: any;
    onComplete: () => void;
    actionProgress: any;
}

export default function TaskComponent(props: ITaskComponent) {
    const { activeTask, onComplete, actionProgress } = props;
    const { CreatedAt, Status } = activeTask;
    const { contractDocument, markAsSigned, triggerSigning, isUpdatingStatus, isTriggeringSigning, refreshContract } =
        useContractSigning();
    const { project } = useProject();
    const { useGenerateContract } = useDocument();
    const { useGenerateClaimsEstimate } = useReport();
    const { showSuccess, showError } = useNotification();
    const generateContractSvc = useGenerateContract();
    const generateClaimsEstimateSvc = useGenerateClaimsEstimate();

    const handleRegenerateContract = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateContractSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Contract regenerated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error regenerating contract:", error);
            showError("Failed to regenerate contract");
        }
    };

    const handleDownloadClaimsEstimate = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateClaimsEstimateSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Claims Estimate downloaded successfully");
        } catch (error) {
            console.error("Error downloading claims estimate:", error);
            showError("Failed to download claims estimate");
        }
    };

    useEffect(() => {
        const scrollableDiv = document.getElementById("project-details-parent");
        if (scrollableDiv) {
            //scrollableDiv.scrollTop = 0; // or use scrollTo
            scrollableDiv.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography level="h4" sx={{ fontWeight: 500 }}>
                    Contact Signing
                </Typography>

                <Box mt={2} display={"flex"} gap={1} alignItems="center">
                    <Chip
                        color={Status === "COMPLETED" ? "success" : "warning"}
                        sx={{ fontSize: 12, border: "1px solid", borderColor: "divider" }}
                    >
                        {Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                    {contractDocument?.Status === "GENERATED" && (
                        <Button
                            size="sm"
                            variant="soft"
                            color="primary"
                            startDecorator={<Mail size={16} />}
                            onClick={triggerSigning}
                            loading={isTriggeringSigning}
                            sx={{ ml: "auto" }}
                        >
                            Send for signing
                        </Button>
                    )}
                    {contractDocument?.Status !== "SIGNED" && contractDocument?.Status !== "GENERATED" && (
                        <Button
                            size="sm"
                            variant="soft"
                            color="success"
                            startDecorator={<CheckCircle2 size={16} />}
                            onClick={markAsSigned}
                            loading={isUpdatingStatus}
                            sx={{ ml: "auto" }}
                        >
                            Mark as signed
                        </Button>
                    )}
                </Box>

                <Box mt={3}>
                    <Box flex={1}>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Description
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            The primary goal of the Contract Signing stage is to finalize the agreement between all
                            parties, ensuring mutual understanding of project terms, responsibilities, and expectations.
                            This stage formalizes commitments, secures approvals, and provides a legal framework for the
                            roofing project, ensuring a smooth execution with clearly defined obligations.
                        </Typography>
                    </Box>
                </Box>
                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Documents Review
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            Material quantities are automatically determined based on the roof area and other factors.
                            Manual modifications are not recommended to ensure accuracy in estimations and cost
                            calculations.
                        </Typography>
                    </Box>
                    <Box mb={1} mt={3} display={"flex"} gap={1}>
                        {contractDocument &&
                            (() => {
                                const isGenerated =
                                    contractDocument.DocumentAssetId && contractDocument.Status === "GENERATED";
                                const menuActions: IMenuAction[] = [];

                                if (isGenerated) {
                                    menuActions.push({
                                        label: "Regenerate",
                                        icon: <RotateCw size={16} />,
                                        onClick: handleRegenerateContract,
                                        disabled: generateContractSvc.isPending,
                                    });
                                }

                                return (
                                    <DocumentPreviewWidget
                                        thumbnailUrl={contractDocument.thumbnailUrl}
                                        title="Contract"
                                        description="Formal agreement to define project deliverables, timelines, and obligations"
                                        status={contractDocument.Status}
                                        assetId={contractDocument.DocumentAssetId}
                                        menuActions={menuActions}
                                    />
                                );
                            })()}
                    </Box>
                </Box>

                {/* Claims Estimate Report Section */}
                <Box mt={3}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Claims Estimate Report
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            Generate a detailed PDF estimate report for insurance claims. Includes property details,
                            itemized materials and labor, pricing breakdown, and total replacement cost value.
                        </Typography>
                    </Box>
                    <Box mt={2}>
                        <Button
                            size="sm"
                            variant="soft"
                            color="primary"
                            startDecorator={<Download size={16} />}
                            onClick={handleDownloadClaimsEstimate}
                            loading={generateClaimsEstimateSvc.isPending}
                        >
                            Download Estimate
                        </Button>
                    </Box>
                </Box>
                <TaskActionComponent
                    taskName={"Contact Signing"}
                    onComplete={onComplete}
                    actionProgress={actionProgress}
                    isNextEligible={contractDocument?.Status === "SIGNED"}
                />
            </Box>
        </Box>
    );
}
